import React, { useEffect, useId, useMemo, useRef, useState } from 'react'
import { Check, ChevronDown } from 'lucide-react'
import {
  DirectoryResponse,
  DirectoryUser,
  UserWithRoles,
  getDirectory,
} from '../../api/usersService'
import { searchDirectory, secondaryLine } from '../../utils/directorySearch'

/*
  The whole directory (~1,500 people) is scrollable, but only the rows in view,
  plus OVERSCAN either side, are in the DOM: rendering every row would make each
  keystroke in the search box re-render all of them.

  Every row is one truncated name line over one truncated detail line, so every
  row is ROW_HEIGHT tall, and the list's height is the constant LIST_HEIGHT.
  Nothing is measured: which rows to draw follows from scrollTop alone. (The
  console's version measured its scroll element through a ref inside a portal
  that mounted later, and drew a blank list.) These numbers must match
  _entra-picker.scss.
*/
export const ROW_HEIGHT = 48
export const LIST_HEIGHT = 288
export const OVERSCAN = 8

// The directory is the same for every admin and the server caches it too, so
// one read serves every opening of the add-user form. A failed read is not kept.
let directoryRequest: Promise<DirectoryResponse> | null = null

const loadDirectory = (): Promise<DirectoryResponse> => {
  if (!directoryRequest) {
    directoryRequest = getDirectory().catch(e => {
      directoryRequest = null
      throw e
    })
  }
  return directoryRequest
}

// For tests: forget the cached directory.
export const resetDirectoryCache = () => {
  directoryRequest = null
}

// The rows to render for a scroll position: [first, last).
export const visibleRange = (
  scrollTop: number,
  count: number
): [number, number] => [
  Math.max(0, Math.floor(scrollTop / ROW_HEIGHT) - OVERSCAN),
  Math.min(count, Math.ceil((scrollTop + LIST_HEIGHT) / ROW_HEIGHT) + OVERSCAN),
]

// The scrollTop that brings row `index` fully into view, moving as little as
// possible; `scrollTop` itself when it already is.
export const scrollTopFor = (index: number, scrollTop: number): number => {
  const top = index * ROW_HEIGHT
  const bottom = top + ROW_HEIGHT
  if (top < scrollTop) return top
  if (bottom > scrollTop + LIST_HEIGHT) return bottom - LIST_HEIGHT
  return scrollTop
}

const serverMessage = (e: unknown): string => {
  const detail = (e as { response?: { data?: { detail?: string } } })?.response
    ?.data?.detail
  return typeof detail === 'string' && detail ? detail : 'Please try again.'
}

interface EntraUserPickerProps {
  value: DirectoryUser | null
  onChange: (user: DirectoryUser) => void
  // BreadGPT's users, so people who already have one are shown as taken.
  existingUsers: UserWithRoles[]
  disabled?: boolean
}

// Pick a person from Entra. What the server stores is their UPN — the address
// every app signs them in by — read from Entra by the object id picked here, so
// a mistyped or mailbox address cannot be entered at all.
const EntraUserPicker: React.FC<EntraUserPickerProps> = ({
  value,
  onChange,
  existingUsers,
  disabled = false,
}) => {
  const [directory, setDirectory] = useState<DirectoryResponse | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [showAll, setShowAll] = useState(false)
  const [active, setActive] = useState(0)
  const [scrollTop, setScrollTop] = useState(0)
  const listRef = useRef<HTMLDivElement>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  const listId = useId()
  const optionId = (index: number) => `${listId}-option-${index}`

  useEffect(() => {
    let cancelled = false
    loadDirectory()
      .then(d => !cancelled && setDirectory(d))
      .catch(e => !cancelled && setLoadError(serverMessage(e)))
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (open) searchRef.current?.focus()
  }, [open])

  // Whether a directory entry already has a BreadGPT user: by Entra id, or by
  // UPN for a row not linked yet.
  const isTaken = useMemo(() => {
    const ids = new Set<string>()
    const emails = new Set<string>()
    for (const u of existingUsers) {
      if (u.entra_object_id) ids.add(u.entra_object_id.toLowerCase())
      emails.add(u.email.toLowerCase())
    }
    return (d: DirectoryUser) =>
      ids.has(d.id.toLowerCase()) ||
      emails.has(d.user_principal_name.toLowerCase())
  }, [existingUsers])

  const fullProfile = directory?.full_profile ?? false
  const candidates = useMemo(() => {
    const users = directory?.users ?? []
    return fullProfile && !showAll ? users.filter(u => u.is_person) : users
  }, [directory, fullProfile, showAll])

  const matches = useMemo(
    () => searchDirectory(candidates, search),
    [candidates, search]
  )
  const count = matches.length
  const [first, last] = visibleRange(scrollTop, count)

  const scrollListTo = (top: number) => {
    if (listRef.current) listRef.current.scrollTop = top
    setScrollTop(top)
  }

  // A new search or filter starts from the top of its results.
  const restart = () => {
    setActive(0)
    scrollListTo(0)
  }

  const moveTo = (index: number) => {
    if (count === 0) return
    const bounded = Math.max(0, Math.min(index, count - 1))
    setActive(bounded)
    // The row may not be rendered yet, but where it would be is index * ROW_HEIGHT.
    const current = listRef.current?.scrollTop ?? scrollTop
    const top = scrollTopFor(bounded, current)
    if (top !== current) scrollListTo(top)
  }

  const select = (user: DirectoryUser) => {
    if (isTaken(user)) return
    onChange(user)
    setOpen(false)
    setSearch('')
    restart()
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      moveTo(active + 1)
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      moveTo(active - 1)
    } else if (e.key === 'Enter') {
      e.preventDefault()
      if (matches[active]) select(matches[active])
    } else if (e.key === 'Escape') {
      // Close the picker, not the settings dialog it sits in.
      e.stopPropagation()
      setOpen(false)
    }
  }

  const renderBody = () => {
    if (loadError) {
      return (
        <p className="entra-picker__message entra-picker__message--error">
          Could not load people from Entra: {loadError}
        </p>
      )
    }
    if (!directory) {
      return <p className="entra-picker__message">Loading people…</p>
    }
    if (count === 0) {
      return <p className="entra-picker__message">No one matches.</p>
    }
    return (
      <div
        ref={listRef}
        className="entra-picker__list"
        style={{ height: Math.min(LIST_HEIGHT, count * ROW_HEIGHT) }}
        onScroll={e => setScrollTop(e.currentTarget.scrollTop)}
        data-testid="entra-picker-list"
      >
        <ul
          id={listId}
          role="listbox"
          aria-label="People"
          className="entra-picker__rows"
          style={{ height: count * ROW_HEIGHT }}
        >
          {matches.slice(first, last).map((user, offset) => {
            const index = first + offset
            const taken = isTaken(user)
            const selected = value?.id === user.id
            const classes = ['entra-picker__option']
            if (index === active && !taken) classes.push('entra-picker__option--active')
            if (taken) classes.push('entra-picker__option--taken')
            return (
              <li
                key={user.id}
                id={optionId(index)}
                role="option"
                aria-selected={selected}
                aria-disabled={taken}
                // Most rows are not in the DOM, so say where this one sits.
                aria-setsize={count}
                aria-posinset={index + 1}
                className={classes.join(' ')}
                style={{ top: index * ROW_HEIGHT }}
                // Keep focus in the search box, so the keys keep working.
                onMouseDown={e => e.preventDefault()}
                onMouseMove={() => index !== active && setActive(index)}
                onClick={() => select(user)}
              >
                <span className="entra-picker__check" aria-hidden="true">
                  {selected && <Check size={14} />}
                </span>
                <span className="entra-picker__text">
                  <span className="entra-picker__name">
                    {user.display_name}
                    {taken && (
                      <span className="entra-picker__taken">already added</span>
                    )}
                  </span>
                  <span className="entra-picker__detail">
                    {secondaryLine(user)}
                  </span>
                </span>
              </li>
            )
          })}
        </ul>
      </div>
    )
  }

  const activeVisible = count > 0 && active >= first && active < last

  return (
    <div className="entra-picker">
      <button
        type="button"
        className="entra-picker__trigger"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        disabled={disabled}
        onClick={() => setOpen(o => !o)}
      >
        {value ? (
          <span className="entra-picker__text">
            <span className="entra-picker__name">{value.display_name}</span>
            <span className="entra-picker__detail">
              {value.user_principal_name}
            </span>
          </span>
        ) : (
          <span className="entra-picker__placeholder">
            Search people by name or email…
          </span>
        )}
        <ChevronDown size={16} className="entra-picker__chevron" />
      </button>

      {open && (
        <div className="entra-picker__panel">
          <input
            ref={searchRef}
            className="entra-picker__search"
            value={search}
            placeholder="Name, email, team…"
            aria-label="Search people"
            aria-controls={listId}
            aria-autocomplete="list"
            aria-activedescendant={activeVisible ? optionId(active) : undefined}
            onChange={e => {
              setSearch(e.target.value)
              restart()
            }}
            onKeyDown={handleKeyDown}
          />
          {renderBody()}
          {directory && !loadError && (
            <div className="entra-picker__footer">
              <span>
                {count.toLocaleString('en-GB')}{' '}
                {search.trim()
                  ? count === 1
                    ? 'match'
                    : 'matches'
                  : count === 1
                    ? 'person'
                    : 'people'}
              </span>
              {fullProfile ? (
                <label className="entra-picker__show-all">
                  <input
                    type="checkbox"
                    checked={showAll}
                    onChange={e => {
                      setShowAll(e.target.checked)
                      restart()
                    }}
                  />
                  Include shared mailboxes and unlicensed accounts
                </label>
              ) : (
                <span>Accounts of people who have left may still be listed.</span>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default EntraUserPicker
